import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { publicImageUrl } from '../lib/media';
import { useAuth } from './AuthContext';

const SettingsContext = createContext(null);

const DEFAULT_SETTINGS = {
  business_name: 'DutiLyne',
  tagline: 'AI-Powered Pharmacy Management',
  primary_color: '#14b8a6',
  accent_color: '#e8590c',
  logo_url: '',
  business_address: '',
  contact_phone: '',
  contact_email: '',
  module_toggles: {
    sales: true, inventory: true, clinical: true, ai_tools: true,
    procurement: true, finance: true, crm: true, admin: true, customer_engagement: true,
  },
  locale: {
    country: 'GH', currency_code: 'GHS', currency_symbol: 'GH₵',
    date_format: 'DD/MM/YYYY', number_locale: 'en-GH', timezone: 'Africa/Accra',
  },
};

function hexToHsl(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function applyThemeColors(primaryHex, accentHex) {
  const root = document.documentElement;

  const primaryHsl = hexToHsl(primaryHex);
  if (primaryHsl) {
    const lightVal = `${primaryHsl.h} ${primaryHsl.s}% ${primaryHsl.l}%`;
    const darkL = Math.min(primaryHsl.l + 11, 70);
    const darkS = Math.max(primaryHsl.s - 12, 20);
    const darkPrimary = `${primaryHsl.h} ${darkS}% ${darkL}%`;
    root.style.setProperty('--primary', lightVal);
    root.style.setProperty('--ring', lightVal);
    root.style.setProperty('--chart-1', lightVal);

    // Build dark overrides
    let darkCss = `--primary: ${darkPrimary}; --ring: ${darkPrimary}; --chart-1: ${darkPrimary};`;

    const accentHsl = hexToHsl(accentHex);
    if (accentHsl) {
      const accentLight = `${accentHsl.h} ${accentHsl.s}% ${accentHsl.l}%`;
      const accentDarkL = Math.min(accentHsl.l + 7, 65);
      const accentDark = `${accentHsl.h} ${accentHsl.s}% ${accentDarkL}%`;
      root.style.setProperty('--accent', accentLight);
      root.style.setProperty('--chart-2', accentLight);
      darkCss += ` --accent: ${accentDark}; --chart-2: ${accentDark};`;
    }

    let darkStyle = document.getElementById('dynamic-dark-theme');
    if (!darkStyle) {
      darkStyle = document.createElement('style');
      darkStyle.id = 'dynamic-dark-theme';
      document.head.appendChild(darkStyle);
    }
    darkStyle.textContent = `.dark { ${darkCss} }`;
  }
}

function mergeSettings(fetched) {
  return {
    ...DEFAULT_SETTINGS,
    ...fetched,
    logo_url: publicImageUrl(fetched?.logo_url || ''),
    module_toggles: { ...DEFAULT_SETTINGS.module_toggles, ...(fetched?.module_toggles || {}) },
    locale: { ...DEFAULT_SETTINGS.locale, ...(fetched?.locale || {}) },
  };
}

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('pharmxai_settings');
      return cached ? mergeSettings(JSON.parse(cached)) : DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  });
  const fetchedForUser = useRef(null);

  // Apply theme on mount and whenever settings change
  useEffect(() => {
    if (settings?.primary_color) {
      applyThemeColors(settings.primary_color, settings.accent_color);
    }
  }, [settings?.primary_color, settings?.accent_color]);

  // Fetch settings as soon as a user is authenticated — fires right after
  // login (no full page reload needed) and again if the signed-in user changes.
  useEffect(() => {
    if (!user) { fetchedForUser.current = null; return; }
    if (fetchedForUser.current === user.user_id) return;
    fetchedForUser.current = user.user_id;
    api.get('/settings').then(res => {
      const merged = mergeSettings(res.data);
      setSettings(merged);
      localStorage.setItem('pharmxai_settings', JSON.stringify(merged));
    }).catch(() => {});
  }, [user]);

  const updateSettings = useCallback(async (newSettings) => {
    // Do not report/save new branding locally until the server accepts it.
    try {
      const res = await api.put('/settings', newSettings);
      const serverMerged = mergeSettings(res.data);
      setSettings(serverMerged);
      localStorage.setItem('pharmxai_settings', JSON.stringify(serverMerged));
      return { success: true, data: serverMerged };
    } catch (err) {
      return { success: false, error: err };
    }
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      const res = await api.get('/settings');
      const merged = mergeSettings(res.data);
      setSettings(merged);
      localStorage.setItem('pharmxai_settings', JSON.stringify(merged));
    } catch (err) {
      console.error('Failed to refresh settings:', err);
    }
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
};
